import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Options for the useManualData hook
 */
export interface ManualDataOptions<T> {
  /**
   * Function that fetches the data
   */
  fetchFn: () => Promise<T>;
  
  /**
   * Initial data value
   */
  initialData?: T;
  
  /**
   * Dependencies that will trigger a refetch when changed
   */
  dependencies?: any[];
  
  /**
   * Whether to perform initial data fetch automatically
   * @default true
   */
  fetchOnMount?: boolean;
  
  /**
   * Whether to only update state if data has changed
   * @default true
   */
  onlyUpdateIfChanged?: boolean;
  
  /**
   * Function to compare old and new data to determine if state should update
   * By default uses JSON.stringify for comparison
   */
  compareDataFn?: (oldData: T | undefined, newData: T) => boolean;
}

/**
 * Hook for manual data fetching without polling
 * Provides loading states, error handling, and manual refetch functionality
 */
export function useManualData<T>(options: ManualDataOptions<T>) {
  const {
    fetchFn,
    initialData,
    dependencies = [],
    fetchOnMount = true,
    onlyUpdateIfChanged = true,
    compareDataFn
  } = options;

  const [data, setData] = useState<T | undefined>(initialData);
  const [loading, setLoading] = useState(fetchOnMount);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  // Use ref to avoid unnecessary re-renders when checking if data changed
  const dataRef = useRef<T | undefined>(initialData);
  
  const shouldUpdateData = useCallback((oldData: T | undefined, newData: T): boolean => {
    if (!onlyUpdateIfChanged) return true;
    
    if (compareDataFn) {
      return compareDataFn(oldData, newData);
    }
    
    return JSON.stringify(oldData) !== JSON.stringify(newData);
  }, [onlyUpdateIfChanged, compareDataFn]);

  const fetchData = useCallback(async (isRefresh = false, silent = false) => {
    try {
      // Only set loading/refreshing states if not silent
      if (!silent) {
        if (!isRefresh) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }
      }
      
      const newData = await fetchFn();
      
      if (shouldUpdateData(dataRef.current, newData)) {
        setData(newData);
        dataRef.current = newData;
        setLastUpdate(new Date());
      }
      
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      // Only set error state if not silent
      if (!silent) {
        setError(errorMessage);
      }
      console.error('Error in useManualData:', err);
    } finally {
      // Only update loading/refreshing states if not silent
      if (!silent) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [fetchFn, shouldUpdateData]);
  
  // Initial fetch when dependencies change
  useEffect(() => {
    if (fetchOnMount) {
      fetchData(false, false);
    }
  }, [...dependencies]);
  
  return {
    data,
    loading,
    refreshing,
    error,
    lastUpdate,
    refetch: useCallback((silent = false) => fetchData(true, silent), [fetchData]),
    silentRefetch: useCallback(() => fetchData(true, true), [fetchData]),
    /** 
     * Allows manual update of data without fetching
     * Useful when you've updated data locally and want to reflect that
     */
    updateData: useCallback((newData: T) => {
      setData(newData);
      dataRef.current = newData;
      setLastUpdate(new Date());
    }, [])
  };
} 